import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, CheckCircle2, Upload, Shield } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const LOAN_TYPES = ["Personal Loan","Home Loan","Business Loan","MSME Loan","CV / CE Loan","Loan Against Property","CC / OD Facility"];

export default function LoanWizard({ onDone, defaultLoanType = "Personal Loan" }) {
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const [data, setData] = useState({
    full_name: "", pan: "", aadhaar: "", mobile: "", email: "",
    employment_type: "Salaried", monthly_income: "", loan_amount: "",
    loan_type: defaultLoanType, tenure_months: 60,
    otp: "", document_name: "", document_base64: "", consent: false,
  });
  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  useEffect(() => {
    api.get("/otp/status").then(r => setOtpEnabled(!!r.data?.enabled)).catch(() => setOtpEnabled(false));
  }, []);

  const STEPS = otpEnabled
    ? ["Basics", "Mobile OTP", "Income", "Documents", "Review"]
    : ["Basics", "Income", "Documents", "Review"];

  const canNext = () => {
    const labels = STEPS;
    if (labels[step] === "Basics") return data.full_name.length >= 3 && /^\S+@\S+\.\S+$/.test(data.email) && /^\+?\d{10,13}$/.test(data.mobile.replace(/\s+/g,""));
    if (labels[step] === "Mobile OTP") return otpVerified;
    if (labels[step] === "Income") return Number(data.monthly_income) > 0 && Number(data.loan_amount) > 0;
    if (labels[step] === "Documents") return true;
    if (labels[step] === "Review") return data.consent;
    return true;
  };

  const sendOTP = async () => {
    setOtpSending(true);
    try {
      const phone = data.mobile.startsWith("+") ? data.mobile : `+91${data.mobile.replace(/\D/g,"")}`;
      await api.post("/otp/send", { phone });
      setOtpSent(true);
      toast.success("OTP sent successfully");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to send OTP");
    } finally { setOtpSending(false); }
  };

  const verifyOTP = async () => {
    try {
      const phone = data.mobile.startsWith("+") ? data.mobile : `+91${data.mobile.replace(/\D/g,"")}`;
      await api.post("/otp/verify", { phone, code: data.otp });
      setOtpVerified(true);
      toast.success("Mobile verified");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid OTP");
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2_000_000) { toast.error("File too large (max 2 MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => { set("document_name", f.name); set("document_base64", String(reader.result || "")); };
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const phone = data.mobile.startsWith("+") ? data.mobile : `+91${data.mobile.replace(/\D/g,"")}`;
      await api.post("/leads", {
        ...data, mobile: phone,
        monthly_income: Number(data.monthly_income),
        loan_amount: Number(data.loan_amount),
        tenure_months: Number(data.tenure_months),
      });
      toast.success("Application submitted!");
      setStep(STEPS.length);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (step === STEPS.length) {
    return (
      <div className="p-10 text-center">
        <div className="mx-auto h-16 w-16 rounded-full bg-saffron/15 grid place-items-center">
          <CheckCircle2 className="h-8 w-8 text-saffron"/>
        </div>
        <h3 className="font-display text-2xl font-bold mt-4">Application received</h3>
        <p className="text-ink-muted mt-2 max-w-md mx-auto">Your reference will be sent to {data.email}. A relationship manager will reach out within 24 hours.</p>
        <Button data-testid="wizard-close" className="btn-saffron mt-6 rounded-full px-6" onClick={onDone}>Close</Button>
      </div>
    );
  }

  const stepLabel = STEPS[step];

  return (
    <div className="flex flex-col">
      <div className="px-6 sm:px-8 pt-6 pb-3 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline">Step {step+1} of {STEPS.length}</div>
            <h3 className="font-display text-xl sm:text-2xl font-bold mt-1">{stepLabel}</h3>
          </div>
          <div className="hidden sm:flex items-center gap-2 text-xs text-ink-muted"><Shield className="h-4 w-4 text-saffron"/> Bank-grade encryption</div>
        </div>
        <Progress value={((step+1)/STEPS.length)*100} className="mt-4 h-1.5"/>
      </div>

      <div className="p-6 sm:p-8 min-h-[280px]">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.22 }} className="space-y-4">
            {stepLabel === "Basics" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full Name"><Input data-testid="wz-name" value={data.full_name} onChange={e=>set("full_name", e.target.value)} placeholder="Riya Sharma"/></Field>
                <Field label="Loan Type">
                  <Select value={data.loan_type} onValueChange={v=>set("loan_type", v)}>
                    <SelectTrigger data-testid="wz-loan-type"><SelectValue/></SelectTrigger>
                    <SelectContent>{LOAN_TYPES.map(t=> <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Mobile (with country code)"><Input data-testid="wz-mobile" value={data.mobile} onChange={e=>set("mobile", e.target.value)} placeholder="+91 98xxxxxxxx"/></Field>
                <Field label="Email"><Input data-testid="wz-email" type="email" value={data.email} onChange={e=>set("email", e.target.value)} placeholder="you@email.com"/></Field>
                <Field label="PAN (optional)"><Input data-testid="wz-pan" value={data.pan} onChange={e=>set("pan", e.target.value.toUpperCase())} placeholder="ABCDE1234F"/></Field>
                <Field label="Aadhaar last 4 (optional)"><Input data-testid="wz-aadhaar" value={data.aadhaar} onChange={e=>set("aadhaar", e.target.value)} placeholder="••••" maxLength={4}/></Field>
              </div>
            )}

            {stepLabel === "Mobile OTP" && (
              <div className="space-y-5">
                <p className="text-sm text-ink-muted">We'll send a 6-digit OTP to <strong>{data.mobile}</strong>.</p>
                {!otpSent && (
                  <Button data-testid="wz-send-otp" onClick={sendOTP} disabled={otpSending} className="btn-saffron rounded-full">
                    {otpSending && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Send OTP
                  </Button>
                )}
                {otpSent && (
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-ink-muted">Enter OTP</Label>
                    <div className="mt-2">
                      <InputOTP maxLength={6} value={data.otp} onChange={v=>set("otp", v)} data-testid="wz-otp-input">
                        <InputOTPGroup>{[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}</InputOTPGroup>
                      </InputOTP>
                    </div>
                    <div className="mt-4 flex gap-3">
                      <Button data-testid="wz-verify-otp" onClick={verifyOTP} disabled={data.otp.length !== 6 || otpVerified} className="btn-saffron rounded-full">
                        {otpVerified ? "Verified" : "Verify"}
                      </Button>
                      <Button variant="ghost" onClick={sendOTP} disabled={otpSending}>Resend</Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {stepLabel === "Income" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Employment Type">
                  <Select value={data.employment_type} onValueChange={v=>set("employment_type", v)}>
                    <SelectTrigger data-testid="wz-emp"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Salaried">Salaried</SelectItem>
                      <SelectItem value="Self-Employed">Self-Employed</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Monthly Income (₹)"><Input data-testid="wz-income" type="number" value={data.monthly_income} onChange={e=>set("monthly_income", e.target.value)} placeholder="75000"/></Field>
                <Field label="Loan Amount Required (₹)"><Input data-testid="wz-amount" type="number" value={data.loan_amount} onChange={e=>set("loan_amount", e.target.value)} placeholder="500000"/></Field>
                <Field label="Tenure (months)"><Input data-testid="wz-tenure" type="number" value={data.tenure_months} onChange={e=>set("tenure_months", e.target.value)} placeholder="60"/></Field>
              </div>
            )}

            {stepLabel === "Documents" && (
              <div>
                <p className="text-sm text-ink-muted mb-3">Upload Income Proof or ID Proof (PDF/JPG/PNG, max 2 MB). Optional.</p>
                <label className="flex items-center justify-center gap-3 border-2 border-dashed border-slate-300 rounded-2xl p-8 cursor-pointer hover:border-saffron hover:bg-saffron/5 transition-colors">
                  <Upload className="h-5 w-5 text-saffron"/>
                  <span className="text-sm font-medium">{data.document_name || "Click to choose a file"}</span>
                  <input data-testid="wz-doc" type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" onChange={onFile}/>
                </label>
              </div>
            )}

            {stepLabel === "Review" && (
              <div className="space-y-4">
                <div className="rounded-2xl border border-slate-200 p-5 bg-slate-50">
                  <div className="grid grid-cols-2 gap-y-2 text-sm">
                    <div className="text-ink-muted">Name</div><div className="font-medium">{data.full_name}</div>
                    <div className="text-ink-muted">Loan Type</div><div className="font-medium">{data.loan_type}</div>
                    <div className="text-ink-muted">Amount</div><div className="font-medium">₹{Number(data.loan_amount).toLocaleString("en-IN")}</div>
                    <div className="text-ink-muted">Tenure</div><div className="font-medium">{data.tenure_months} months</div>
                    <div className="text-ink-muted">Mobile</div><div className="font-medium">{data.mobile}{otpEnabled && otpVerified ? " ✓" : ""}</div>
                    <div className="text-ink-muted">Email</div><div className="font-medium">{data.email}</div>
                    <div className="text-ink-muted">Monthly Income</div><div className="font-medium">₹{Number(data.monthly_income).toLocaleString("en-IN")}</div>
                    <div className="text-ink-muted">Employment</div><div className="font-medium">{data.employment_type}</div>
                  </div>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox data-testid="wz-consent" checked={data.consent} onCheckedChange={(v)=>set("consent", !!v)} className="mt-0.5"/>
                  <span className="text-sm text-ink-muted">I authorise TARA Finserv to access my credit information and share details with lending partners.</span>
                </label>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="px-6 sm:px-8 py-4 border-t border-slate-200 flex items-center justify-between gap-3">
        <Button variant="ghost" data-testid="wz-back" disabled={step===0} onClick={()=>setStep(s=>Math.max(0,s-1))}>Back</Button>
        {step < STEPS.length - 1 ? (
          <Button data-testid="wz-next" className="btn-saffron rounded-full px-6" disabled={!canNext()} onClick={()=>setStep(s=>s+1)}>Continue</Button>
        ) : (
          <Button data-testid="wz-submit" className="btn-saffron rounded-full px-6" disabled={!canNext() || submitting} onClick={submit}>
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>} Submit Application
          </Button>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wider text-ink-muted">{label}</Label>
      {children}
    </div>
  );
}
